import { useState, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Sparkles, Send, ChevronDown, ChevronUp, Loader2, RotateCcw } from 'lucide-react';
import api from '@/lib/api';

interface AIMessage {
  role: 'user' | 'assistant';
  content: string;
  actions?: Array<{ type: string; details: string }>;
}

const ACTION_LABELS: Record<string, string> = {
  assign_slot: 'Assigned slot',
  reassign_slot: 'Reassigned slot',
  delete_slot: 'Deleted slot',
  create_recurring_template: 'Created template',
  delete_template: 'Deleted template',
  send_chat_message: 'Sent message',
};

const STORAGE_KEY = 'kaiwatch_ai_chat';

function loadMessages(): AIMessage[] {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function AIChatBot() {
  const queryClient = useQueryClient();
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<AIMessage[]>(loadMessages);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = (smooth = true) => {
    requestAnimationFrame(() => {
      const el = scrollContainerRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  };

  useEffect(() => {
    scrollToBottom();
    // Persist messages to sessionStorage so they survive tab switches
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (isExpanded) {
      inputRef.current?.focus();
      scrollToBottom(false);
    }
  }, [isExpanded]);

  const handleSend = async () => {
    const content = input.trim();
    if (!content || isLoading) return;

    const userMsg: AIMessage = { role: 'user', content };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setIsLoading(true);

    try {
      // Send last 10 messages as conversation history
      const history = updatedMessages.slice(-10).map((m) => ({
        role: m.role,
        content: m.content,
      }));
      // Remove the last message (current user message) from history since it's sent separately
      history.pop();

      const res = await api.post('/ai/chat', {
        message: content,
        conversation_history: history,
      });

      const assistantMsg: AIMessage = {
        role: 'assistant',
        content: res.data.response,
        actions: res.data.actions,
      };
      setMessages((prev) => [...prev, assistantMsg]);

      // Invalidate all data queries when AI performed actions
      if (res.data.actions?.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['timeslots'] });
        queryClient.invalidateQueries({ queryKey: ['monthly-summary'] });
        queryClient.invalidateQueries({ queryKey: ['availability'] });
        queryClient.invalidateQueries({ queryKey: ['availability-templates'] });
        queryClient.invalidateQueries({ queryKey: ['my-slots'] });
        queryClient.invalidateQueries({ queryKey: ['payment-alert'] });
        queryClient.invalidateQueries({ queryKey: ['users'] });
        queryClient.invalidateQueries({ queryKey: ['messages'] });
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Something went wrong';
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Error: ${errorMessage}. Please try again.` },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Render a simple inline bar chart from chart_data JSON
  const renderBarChart = (chartJson: string, key: number) => {
    try {
      const chart = JSON.parse(chartJson) as { type: string; title: string; labels: string[]; values: number[] };
      const maxVal = Math.max(...chart.values, 1);
      const colors = ['#8B5CF6', '#10B981', '#6366F1', '#F59E0B', '#EC4899', '#3B82F6'];
      return (
        <div key={key} className="my-2 p-2 bg-slate-800 rounded-lg">
          <div className="text-xs font-medium text-gray-300 mb-2">{chart.title}</div>
          <div className="space-y-1">
            {chart.labels.map((label, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px]">
                <span className="text-gray-400 w-20 text-right truncate flex-shrink-0">{label}</span>
                <div className="flex-1 h-4 bg-slate-700 rounded overflow-hidden">
                  <div
                    className="h-full rounded transition-all"
                    style={{
                      width: `${Math.max((chart.values[i] / maxVal) * 100, 2)}%`,
                      backgroundColor: colors[i % colors.length],
                    }}
                  />
                </div>
                <span className="text-gray-300 w-10 text-right flex-shrink-0">{chart.values[i]}</span>
              </div>
            ))}
          </div>
        </div>
      );
    } catch {
      return null;
    }
  };

  // Render markdown table lines as an HTML table
  const renderTable = (tableLines: string[], key: number) => {
    const rows = tableLines
      .filter(line => !line.match(/^\|[\s-:|]+\|$/))
      .map(line =>
        line.split('|').slice(1, -1).map(cell => cell.trim())
      );
    if (rows.length === 0) return null;
    const headerRow = rows[0];
    const bodyRows = rows.slice(1);
    return (
      <div key={key} className="my-2 overflow-x-auto">
        <table className="w-full text-[11px] border-collapse">
          <thead>
            <tr>
              {headerRow.map((cell, i) => (
                <th key={i} className="text-left px-2 py-1 border-b border-slate-600 text-gray-300 font-medium">{cell}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((row, i) => (
              <tr key={i} className="border-b border-slate-700/50">
                {row.map((cell, j) => (
                  <td key={j} className="px-2 py-1 text-gray-400">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render inline text with bold formatting
  const renderInlineText = (text: string) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, j) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={j}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  // Enhanced markdown rendering (bold, lists, tables, charts)
  const renderContent = (content: string) => {
    // Split on [CHART]...[/CHART] blocks first
    const chartPattern = /\[CHART\]([\s\S]*?)\[\/CHART\]/g;
    const segments: Array<{ type: 'text'; value: string } | { type: 'chart'; value: string }> = [];
    let lastIndex = 0;
    let match;

    while ((match = chartPattern.exec(content)) !== null) {
      if (match.index > lastIndex) {
        segments.push({ type: 'text', value: content.slice(lastIndex, match.index) });
      }
      segments.push({ type: 'chart', value: match[1] });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < content.length) {
      segments.push({ type: 'text', value: content.slice(lastIndex) });
    }

    let elementKey = 0;
    const elements: React.ReactNode[] = [];

    for (const segment of segments) {
      if (segment.type === 'chart') {
        elements.push(renderBarChart(segment.value, elementKey++));
        continue;
      }

      // Process text segment: detect tables and render line by line
      const lines = segment.value.split('\n');
      let i = 0;
      while (i < lines.length) {
        // Detect markdown table (at least 2 rows starting with |, with a separator row)
        if (lines[i].startsWith('|') && i + 1 < lines.length && lines[i + 1].match(/^\|[\s-:|]+\|$/)) {
          const tableLines: string[] = [];
          while (i < lines.length && lines[i].startsWith('|')) {
            tableLines.push(lines[i]);
            i++;
          }
          elements.push(renderTable(tableLines, elementKey++));
          continue;
        }

        const line = lines[i];

        // List items
        if (line.startsWith('- ') || line.startsWith('* ')) {
          elements.push(
            <div key={elementKey++} className="flex gap-1.5 ml-2">
              <span className="text-gray-400">&#x2022;</span>
              <span>{renderInlineText(line.replace(/^[-*]\s/, ''))}</span>
            </div>
          );
        } else {
          elements.push(
            <div key={elementKey++}>
              {renderInlineText(line)}
              {i < lines.length - 1 && line === '' && <br />}
            </div>
          );
        }
        i++;
      }
    }

    return elements;
  };

  return (
    <div data-testid="container-ai-chatbot" className="bg-card rounded-xl border border-slate-700 overflow-hidden">
      {/* Toggle header */}
      <div className="flex items-center justify-between p-4 hover:bg-slate-800/50 transition-colors">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          data-testid="clickable-ai-toggle"
          className="flex items-center gap-2 flex-1"
        >
          <Sparkles size={20} className="text-purple-400" />
          <span className="text-white font-semibold">AI Assistant</span>
          <span className="text-xs text-gray-400 bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full">Beta</span>
        </button>
        <div className="flex items-center gap-2">
          {isExpanded && messages.length > 0 && (
            <button
              onClick={() => {
                setMessages([]);
                sessionStorage.removeItem(STORAGE_KEY);
              }}
              className="p-1 text-gray-400 hover:text-white transition-colors"
              title="New chat"
            >
              <RotateCcw size={16} />
            </button>
          )}
          <button onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? (
              <ChevronUp size={20} className="text-gray-400" />
            ) : (
              <ChevronDown size={20} className="text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {/* Chat panel */}
      {isExpanded && (
        <div className="border-t border-slate-700">
          {/* Messages */}
          <div
            ref={scrollContainerRef}
            data-testid="container-ai-messages"
            className="h-72 overflow-y-auto p-4 space-y-3"
          >
            {messages.length === 0 && (
              <div className="text-center text-gray-400 text-sm py-8">
                <Sparkles size={24} className="mx-auto mb-2 text-purple-400/50" />
                <p>Ask me to manage schedules, assign slots, or send messages.</p>
                <p className="text-xs mt-1 text-gray-500">
                  Try: "List all caretakers" or "Assign Sarah to tomorrow 3-5 PM"
                </p>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${
                    msg.role === 'user'
                      ? 'bg-purple-600 text-white rounded-br-md'
                      : 'bg-slate-700 text-gray-200 rounded-bl-md'
                  }`}
                >
                  <div className="whitespace-pre-wrap break-words">
                    {msg.role === 'assistant' ? renderContent(msg.content) : msg.content}
                  </div>

                  {/* Action badges (collapsed duplicates) */}
                  {msg.actions && msg.actions.length > 0 && (() => {
                    const counts = new Map<string, number>();
                    for (const action of msg.actions) {
                      const label = ACTION_LABELS[action.type] || action.type;
                      counts.set(label, (counts.get(label) || 0) + 1);
                    }
                    return (
                      <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-slate-600">
                        {Array.from(counts.entries()).map(([label, count]) => (
                          <span
                            key={label}
                            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300"
                          >
                            {label}{count > 1 && ` x${count}`}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-700 px-3 py-2 rounded-2xl rounded-bl-md">
                  <Loader2 size={16} className="animate-spin text-purple-400" />
                </div>
              </div>
            )}

          </div>

          {/* Input */}
          <div className="p-3 border-t border-slate-700">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask the AI assistant..."
                disabled={isLoading}
                data-testid="input-ai-message"
                className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-full text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                data-testid="clickable-ai-send"
                className="p-2 bg-purple-500 hover:bg-purple-600 text-white rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
